/**
 * Footer newsletter through Shopify Forms, wearing the footer's own clothes.
 *
 * The Forms app renders its form into a shadow root, in its own card, boxed
 * field and block button. The footer already has a newsletter design, so this
 * dresses the app's form in it: once the app has drawn, the stylesheet the
 * section carries in a <template> is adopted into the shadow root, the words
 * come from the section's settings (placeholder, button, success line), and
 * the built-in form — which stays on the page as the fallback until then — is
 * hidden by the `is-live` class.
 *
 * Nothing about the app's behaviour is touched: validation, hCaptcha and the
 * submission are the app's own, and the styles never reach past its root.
 */

const EMBED = 'shopify-forms-embed';
const STYLE_ID = 'brand-footer-forms-style';

class BrandFooterForms extends HTMLElement {
  /** Waits for the app to mount its element inside the container. */
  #mount;

  /** Re-applies the words after each of the app's own renders. */
  #render;

  #embed;

  connectedCallback() {
    this.#mount = new MutationObserver(() => this.#attach());
    this.#mount.observe(this, { childList: true, subtree: true });
    this.#attach();
  }

  disconnectedCallback() {
    this.#mount?.disconnect();
    this.#render?.disconnect();
    this.#embed = undefined;
  }

  #attach() {
    const embed = this.querySelector(EMBED);
    if (!embed || embed === this.#embed || !embed.shadowRoot) return;

    this.#embed = embed;
    this.#mount.disconnect();

    const root = embed.shadowRoot;
    this.#render?.disconnect();
    this.#render = new MutationObserver(() => this.#dress(root));
    this.#render.observe(root, { childList: true, subtree: true });
    this.#dress(root);
  }

  /** @param {ShadowRoot} root */
  #dress(root) {
    const form = root.querySelector('form[data-testid="form"]');
    const success = root.querySelector('[data-testid="banner-success"]');
    // Nothing drawn yet — the definition is still on its way.
    if (!form && !success) return;

    this.#style(root);
    if (form) this.#word(form);
    if (success) this.#wordSuccess(success);
    this.classList.add('is-live');
  }

  /**
   * The section's stylesheet for the shadow tree, appended once. It beats the
   * app's own sheets by specificity alone (every rule is rooted at
   * #app-embed), which is why the template needs no !important.
   * @param {ShadowRoot} root
   */
  #style(root) {
    if (root.getElementById(STYLE_ID)) return;
    const style = this.querySelector('template[data-forms-style]')?.content.querySelector('style');
    if (!style) return;
    const sheet = style.cloneNode(true);
    sheet.id = STYLE_ID;
    root.append(sheet);
  }

  /**
   * The footer's words, not the app's. Each is written only when it differs,
   * so the render this triggers finds nothing left to do.
   * @param {HTMLFormElement} form
   */
  #word(form) {
    const { placeholder, label } = this.dataset;

    const input = form.querySelector('input[data-testid="field-email"]');
    if (input instanceof HTMLInputElement && placeholder && input.placeholder !== placeholder) {
      input.placeholder = placeholder;
    }

    // Left alone while the app is showing its spinner in it.
    const button = form.querySelector('button[data-testid="btn-form-submit"]');
    if (button && label && !button.querySelector('svg') && button.textContent !== label) {
      button.textContent = label;
    }
  }

  /** @param {Element} banner */
  #wordSuccess(banner) {
    const { success } = this.dataset;
    const line = banner.querySelector('p');
    if (line && success && line.textContent !== success) line.textContent = success;
  }
}

if (!customElements.get('brand-footer-forms')) {
  customElements.define('brand-footer-forms', BrandFooterForms);
}
